import { useId, type FormEvent } from "react";
import type { NewCallPayload } from "@/api/mail";
import type { VesselCallInput } from "@/api/vessels";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { addDays } from "@/lib/dates";
import { useFormValues, useSubmitState } from "@/lib/use-form";

export type NewCallInput = VesselCallInput & { invoice_date: string | null };

type NewCallFormProps = {
  payload: NewCallPayload;
  vessels: { id: string; name: string }[];
  clients: { id: string; code: string }[];
  onSubmit: (input: NewCallInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

/** A vessel call as the e-mail described it, for the director to correct before it and
 *  its draft invoice are created. Departure defaults to the day after the ETA. */
export function NewCallForm({
  payload,
  vessels,
  clients,
  onSubmit,
  onCancel,
  onReload,
}: NewCallFormProps) {
  const id = useId();
  const eta = payload.eta ?? "";
  const { values, set } = useFormValues({
    vessel_id: payload.vessel_id,
    client_id: payload.client_id ?? "",
    voyage: payload.voyage,
    terminal: payload.terminal,
    port: payload.port,
    arrival: eta,
    departure: eta ? addDays(eta, 1) : "",
    service_from: eta ? addDays(eta, -1) : "",
    service_to: eta ? addDays(eta, 1) : "",
    invoice_date: "",
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        vessel_id: values.vessel_id,
        client_id: values.client_id,
        voyage: values.voyage.trim(),
        terminal: values.terminal.trim(),
        port: values.port.trim(),
        arrival: values.arrival,
        berth: null,
        departure: values.departure,
        service_from: values.service_from,
        service_to: values.service_to,
        invoice_date: values.invoice_date || null,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <SelectField
          id={`${id}-vessel`}
          label="Vessel"
          value={values.vessel_id}
          onChange={(v) => set("vessel_id", v)}
          options={vessels.map((v) => ({ value: v.id, label: v.name }))}
          error={errors.vessel_id}
        />
        <SelectField
          id={`${id}-client`}
          label="Client"
          value={values.client_id}
          onChange={(v) => set("client_id", v)}
          options={clients.map((c) => ({ value: c.id, label: c.code }))}
          placeholder="Choose a client"
          error={errors.client_id}
        />
        <TextField
          id={`${id}-voyage`}
          label="Voyage"
          value={values.voyage}
          onChange={(v) => set("voyage", v)}
          error={errors.voyage}
        />
        <TextField
          id={`${id}-terminal`}
          label="Terminal"
          value={values.terminal}
          onChange={(v) => set("terminal", v)}
          error={errors.terminal}
        />
        <TextField
          id={`${id}-port`}
          label="Port"
          value={values.port}
          onChange={(v) => set("port", v)}
          error={errors.port}
        />
        <TextField
          id={`${id}-arrival`}
          label="Arrival"
          type="date"
          value={values.arrival}
          onChange={(v) => set("arrival", v)}
          error={errors.arrival}
          hint="The ETA in the e-mail; correct it when the schedule changes"
        />
        <TextField
          id={`${id}-departure`}
          label="Departure"
          type="date"
          value={values.departure}
          onChange={(v) => {
            if (values.service_to === values.departure) set("service_to", v);
            set("departure", v);
          }}
          error={errors.departure}
          hint="A guess until the vessel sails"
        />
        <TextField
          id={`${id}-from`}
          label="Service from"
          type="date"
          value={values.service_from}
          onChange={(v) => set("service_from", v)}
          error={errors.service_from}
        />
        <TextField
          id={`${id}-to`}
          label="Service to"
          type="date"
          value={values.service_to}
          onChange={(v) => set("service_to", v)}
          error={errors.service_to}
        />
        <TextField
          id={`${id}-invoice`}
          label="Draft invoice date"
          type="date"
          value={values.invoice_date}
          onChange={(v) => set("invoice_date", v)}
          error={errors.invoice_date}
          hint="Leave empty for today; it can change until the invoice is issued"
        />
      </div>
      <FormActions saving={saving} submitLabel="Create call and draft" onCancel={onCancel} />
    </form>
  );
}
